import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ElementRef,
  OnDestroy,
} from "@angular/core";
import { ValueLabel } from "../value-label";
import { Overlay, OverlayRef } from "@angular/cdk/overlay";
import { ComponentPortal } from "@angular/cdk/portal";
import { GemFilterContentComponent } from "../gem-filter-content/gem-filter-content.component";
import { Subscription } from "rxjs";

@Component({
  selector: "gem-filter",
  standalone: false,
  styleUrls: ["gem-filter.component.scss"],
  template: `
    <button
      #filterBtn
      class="filter"
      [ngClass]="filterToggle ? 'filter-opened' : 'filter-closed'"
      (click)="toggleDropdown()"
    >
      {{ title }}
    </button>
  `,
})
export class GemFilterComponent implements OnDestroy {
  @Input() title: string = "";
  @Input() searchLabel: string = "Search";
  @Input() options: ValueLabel[] = [];
  @Input() value: ValueLabel[] = [];
  @Input() reset?: { search: boolean };

  @Output() change = new EventEmitter<{ checked: boolean; vl: ValueLabel }>();

  @ViewChild("filterBtn", { static: true }) filterButton!: ElementRef;

  private overlayRef?: OverlayRef;
  private subscriptions: Subscription[] = [];
  private domListeners: (() => void)[] = [];
  filterToggle = false;

  constructor(private overlay: Overlay) {}

  toggleDropdown(): void {
    if (this.overlayRef) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  private openDropdown(): void {
    if (this.overlayRef) return;

    this.overlayRef = this.overlay.create({
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this.filterButton)
        .withPositions([
          { originX: "start", originY: "bottom", overlayX: "start", overlayY: "top", offsetY: 8 },
          { originX: "start", originY: "top", overlayX: "start", overlayY: "bottom", offsetY: -8 },
        ])
        .withFlexibleDimensions(false)
        .withPush(true),
      hasBackdrop: false, // allows scrolling behind
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      panelClass: "gem-filter-overlay-panel",
    });

    const componentPortal = new ComponentPortal(GemFilterContentComponent);
    const componentPortalRef = this.overlayRef.attach(componentPortal);

    // Pass inputs to content component
    componentPortalRef.instance.options = this.options;
    componentPortalRef.instance.value = this.value;
    componentPortalRef.instance.searchLabel = this.searchLabel;
    componentPortalRef.instance.reset = this.reset;

    // Subscribe to selection changes

    const selectionChangeSub = componentPortalRef.instance.selectionChange.subscribe((payload) =>
      this.change.emit(payload),
    );
    const closeSub = componentPortalRef.instance.close.subscribe(() => this.closeDropdown());
    this.subscriptions.push(selectionChangeSub, closeSub);

    // Handle click outside to close dropdown
    const clickOutside = (event: MouseEvent) => {
      const overlayEl = this.overlayRef?.overlayElement;
      if (
        overlayEl &&
        !overlayEl.contains(event.target as Node) &&
        !this.filterButton.nativeElement.contains(event.target as Node)
      ) {
        this.closeDropdown();
      }
    };
    document.addEventListener("click", clickOutside, true);
    this.domListeners.push(() => document.removeEventListener("click", clickOutside, true));

    this.filterToggle = true;
  }

  private closeDropdown(): void {
    this.overlayRef?.dispose();
    this.overlayRef = undefined;
    this.filterToggle = false;

    // Cleanup subscriptions
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.subscriptions = [];

    // Cleanup DOM listeners
    this.domListeners.forEach((remove) => remove());
    this.domListeners = [];
  }

  ngOnDestroy(): void {
    this.closeDropdown();
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
